/**
 * Madrassa — page principale.
 * - Liste les madrassas dont je suis membre (et leur statut)
 * - Permet de demander à rejoindre une madrassa active OU de proposer
 *   la création d'une nouvelle madrassa (validée par l'admin global)
 * - Pour les madrassa_admins, lien vers la gestion détaillée
 *
 * Rappel produit : aucun professeur indépendant n'existe. Le rôle
 * 'teacher' n'est actif que via une membership approuvée.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Plus, Users, Inbox, GraduationCap, Send, ArrowRight, Shield,
  CheckCircle2, Clock, XCircle, RefreshCcw,
} from 'lucide-react';
import { GlassCard, SectionHeader, PageLoading } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { madrassaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ClassStatsPanel } from '../components/madrassa/ClassStatsPanel';
import { MadrassaUsersTab } from '../components/MadrassaUsersTab';

const STATUS_META = {
  pending:   { fr: 'En attente', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30',  icon: Clock },
  approved:  { fr: 'Approuvé',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
  rejected:  { fr: 'Refusé',     cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30',     icon: XCircle },
  removed:   { fr: 'Retiré',     cls: 'bg-surface-elevated text-text-muted border-border',   icon: XCircle },
  active:    { fr: 'Active',     cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2 },
};

const StatusPill = ({ status, testid }) => {
  const meta = STATUS_META[status] || { fr: status, cls: 'border-border', icon: Clock };
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.cls} border text-[10px]`} data-testid={testid}>
      <Icon className="w-3 h-3 mr-1 inline" />
      {meta.fr}
    </Badge>
  );
};

const ROLE_LABEL = { admin: 'Admin', teacher: 'Professeur', student: 'Élève' };

// ---------------------------------------------------------------------------
// Join / Create modals
// ---------------------------------------------------------------------------
const JoinMadrassaDialog = ({ activeMadrassas, onDone }) => {
  const [open, setOpen] = useState(false);
  const [madrassaId, setMadrassaId] = useState('');
  const [role, setRole] = useState('teacher');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!madrassaId) { setError('Choisissez une madrassa.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await madrassaApi.requestJoin({ madrassa_id: madrassaId, role, notes });
      setOpen(false);
      setMadrassaId(''); setRole('teacher'); setNotes('');
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Demande impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="madrassa-join-open">
          <Inbox className="w-3.5 h-3.5 mr-1" />
          Rejoindre une madrassa
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="madrassa-join-dialog">
        <DialogHeader>
          <DialogTitle>Demande de rattachement</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Madrassa</label>
            <select
              className="w-full bg-surface-elevated/40 border border-border rounded-md p-2 text-sm"
              value={madrassaId}
              onChange={(e) => setMadrassaId(e.target.value)}
              data-testid="madrassa-join-select"
            >
              <option value="">— Choisir —</option>
              {activeMadrassas.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-text-muted">Rôle souhaité</label>
            <div className="flex gap-2 mt-1">
              {['teacher', 'student'].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`text-xs px-3 py-1.5 rounded-md border ${
                    role === r ? 'border-primary/60 bg-primary/15 text-primary'
                              : 'border-border bg-surface-elevated/40 text-text-muted'
                  }`}
                  data-testid={`madrassa-join-role-${r}`}
                >
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
          </div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Message à l'admin de la madrassa (optionnel)"
            rows={2}
            data-testid="madrassa-join-notes"
          />
          {error && <p className="text-sm text-rose-300" data-testid="madrassa-join-error">{error}</p>}
          <div className="flex justify-end">
            <Button onClick={submit} disabled={submitting} data-testid="madrassa-join-submit">
              <Send className="w-3.5 h-3.5 mr-1" />
              {submitting ? 'Envoi…' : 'Envoyer la demande'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateMadrassaDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!name.trim()) { setError('Donnez un nom.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await madrassaApi.requestCreate({ name: name.trim(), description: description.trim() || null });
      setOpen(false);
      setName(''); setDescription('');
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Demande impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="madrassa-create-open">
          <Plus className="w-3.5 h-3.5 mr-1" />
          Demander la création d'une madrassa
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="madrassa-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle madrassa</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input
            placeholder="Nom de la madrassa"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="madrassa-create-name"
          />
          <Textarea
            placeholder="Description (optionnel)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            data-testid="madrassa-create-description"
          />
          {error && <p className="text-sm text-rose-300" data-testid="madrassa-create-error">{error}</p>}
          <p className="text-[11px] text-text-muted">
            Votre demande sera examinée par un administrateur global. Vous serez
            automatiquement nommé administrateur de cette madrassa une fois validée.
          </p>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={submitting} data-testid="madrassa-create-submit">
              <Send className="w-3.5 h-3.5 mr-1" />
              {submitting ? 'Envoi…' : 'Envoyer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Teacher onboarding banner — shows the user's current teacher state and
// invites them to request a teacher seat in an active madrassa.
// ---------------------------------------------------------------------------
const TeacherOnboardingBanner = ({ status, partners, onDone }) => {
  const teacherState = status?.teacherState || 'none';
  if (status?.isGlobalAdmin) {
    return null; // Admin already has full access
  }
  if (teacherState === 'active') {
    return (
      <GlassCard data-testid="teacher-onboarding-active">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-300" />
          <span className="text-sm text-text-primary">
            Vous êtes professeur validé dans {status.teachingClassesCount || 'une'} classe{status.teachingClassesCount > 1 ? 's' : ''}.
          </span>
        </div>
      </GlassCard>
    );
  }
  if (teacherState === 'pending') {
    return (
      <GlassCard data-testid="teacher-onboarding-pending">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-amber-300" />
          <span className="text-sm text-text-primary">
            Votre demande de professeur est en attente de validation par la madrassa.
          </span>
        </div>
      </GlassCard>
    );
  }
  return (
    <GlassCard data-testid="teacher-onboarding-none">
      <h4 className="text-sm font-serif text-text-primary mb-2">
        Je suis professeur dans une madrassa
      </h4>
      <p className="text-xs text-text-muted mb-3">
        Choisissez une madrassa partenaire et envoyez votre demande. Votre rôle de
        professeur ne sera actif qu'après validation par l'administration de la madrassa
        (aucun professeur indépendant n'est autorisé).
      </p>
      <TeacherRequestForm partners={partners} onDone={onDone} />
    </GlassCard>
  );
};

const TeacherRequestForm = ({ partners, onDone }) => {
  const [madrassaId, setMadrassaId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!madrassaId) { setError('Choisissez une madrassa.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await madrassaApi.createTeacherRequest(madrassaId, { notes });
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Demande impossible.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="flex flex-wrap gap-2 items-end">
      <select
        className="flex-1 min-w-[220px] bg-surface-elevated/40 border border-border rounded-md p-2 text-sm"
        value={madrassaId}
        onChange={(e) => setMadrassaId(e.target.value)}
        data-testid="teacher-onboarding-select"
      >
        <option value="">— Choisir une madrassa partenaire —</option>
        {partners.map((m) => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
      </select>
      <Input
        className="flex-1 min-w-[180px]"
        placeholder="Mot pour l'admin (optionnel)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        data-testid="teacher-onboarding-notes"
      />
      <Button size="sm" onClick={submit} disabled={submitting}
              data-testid="teacher-onboarding-submit">
        <Send className="w-3.5 h-3.5 mr-1" />
        {submitting ? 'Envoi…' : 'Demander'}
      </Button>
      {error && (
        <p className="text-xs text-rose-300 w-full" data-testid="teacher-onboarding-error">
          {error}
        </p>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Membership row
// ---------------------------------------------------------------------------
const MembershipRow = ({ m, onOpen }) => {
  const isAdminApproved = m.role === 'admin' && m.status === 'approved';
  return (
    <div
      className="rounded-lg border border-border bg-surface-elevated/40 p-4 flex items-center gap-3"
      data-testid={`madrassa-membership-${m.id}`}
    >
      <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
        <Building2 className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-text-primary truncate">
            {m.madrassa?.name || m.madrassa_id}
          </span>
          <Badge variant="outline" className="text-[10px]">
            {ROLE_LABEL[m.role] || m.role}
          </Badge>
          <StatusPill status={m.status} testid={`madrassa-membership-${m.id}-status`} />
        </div>
        {m.notes && (
          <p className="text-xs text-text-muted mt-1 truncate">{m.notes}</p>
        )}
      </div>
      {isAdminApproved && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onOpen}
          data-testid={`madrassa-membership-${m.id}-manage`}
        >
          Gérer <ArrowRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Manage view — when a madrassa_admin opens a madrassa
// ---------------------------------------------------------------------------
const ManageMadrassa = ({ madrassaId, onBack }) => {
  const [info, setInfo] = useState(null);
  const [members, setMembers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('members');
  const [newClass, setNewClass] = useState({ name: '', level: '' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('student');
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvCommitResult, setCsvCommitResult] = useState(null);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [i, m, c] = await Promise.all([
        madrassaApi.get(madrassaId),
        madrassaApi.listMembers(madrassaId),
        madrassaApi.listClasses(madrassaId),
      ]);
      setInfo(i.data);
      setMembers(m.data?.items || []);
      setClasses(c.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Impossible de charger.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [madrassaId]);

  const decide = async (mid, action) => {
    try {
      await madrassaApi.decideMembership(madrassaId, mid, { action });
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || 'Erreur.'); }
  };
  const removeMember = async (mid) => {
    if (!window.confirm('Retirer ce membre ?')) return;
    try { await madrassaApi.removeMember(madrassaId, mid); refresh(); }
    catch (e) { setError(e?.response?.data?.detail || 'Erreur.'); }
  };
  const invite = async () => {
    if (!inviteEmail) return;
    try {
      await madrassaApi.invite(madrassaId, { email: inviteEmail, role: inviteRole });
      setInviteEmail('');
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || 'Invitation impossible.'); }
  };
  const createClass = async () => {
    if (!newClass.name.trim()) return;
    try {
      await madrassaApi.createClass(madrassaId, {
        name: newClass.name.trim(),
        level: newClass.level.trim() || null,
      });
      setNewClass({ name: '', level: '' });
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || 'Création impossible.'); }
  };
  const deleteClass = async (cid) => {
    if (!window.confirm('Supprimer cette classe ?')) return;
    try { await madrassaApi.deleteClass(madrassaId, cid); refresh(); }
    catch (e) { setError(e?.response?.data?.detail || 'Erreur.'); }
  };

  const onPreviewCsv = async () => {
    if (!csvFile) return;
    setError(null);
    setCsvPreview(null); setCsvCommitResult(null);
    const fd = new FormData();
    fd.append('file', csvFile);
    try {
      const r = await madrassaApi.importPreview(madrassaId, fd);
      setCsvPreview(r.data);
    } catch (e) { setError(e?.response?.data?.detail || 'Erreur preview.'); }
  };
  const onCommitCsv = async () => {
    if (!csvFile) return;
    setError(null);
    setCsvCommitResult(null);
    const fd = new FormData();
    fd.append('file', csvFile);
    try {
      const r = await madrassaApi.importCommit(madrassaId, fd);
      setCsvCommitResult(r.data);
      refresh();
    } catch (e) { setError(e?.response?.data?.detail || 'Erreur commit.'); }
  };

  const pending = useMemo(() => members.filter((m) => m.status === 'pending'), [members]);
  const approved = useMemo(() => members.filter((m) => m.status === 'approved'), [members]);

  if (loading) return <PageLoading />;

  return (
    <div className="space-y-4" data-testid="madrassa-manage">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="madrassa-manage-back">
        <ArrowRight className="w-4 h-4 mr-1 rotate-180" /> Retour
      </Button>
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-serif text-text-primary">{info?.name}</h3>
            <StatusPill status={info?.status} />
          </div>
          <Badge variant="outline" className="text-[10px]">
            <Shield className="w-3 h-3 mr-1" />Admin
          </Badge>
        </div>
        {info?.description && (
          <p className="text-sm text-text-muted mt-2">{info.description}</p>
        )}
      </GlassCard>

      {error && (
        <p className="text-sm text-rose-300" data-testid="madrassa-manage-error">{error}</p>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-surface border border-border">
          <TabsTrigger value="members" data-testid="madrassa-manage-tab-members">
            <Users className="w-3.5 h-3.5 mr-1" />
            Membres
            {pending.length > 0 && (
              <Badge variant="outline" className="ml-2 text-[10px]">
                {pending.length} en attente
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="classes" data-testid="madrassa-manage-tab-classes">
            <GraduationCap className="w-3.5 h-3.5 mr-1" />
            Classes ({classes.length})
          </TabsTrigger>
          <TabsTrigger value="teachers" data-testid="madrassa-manage-tab-teachers">
            <Users className="w-3.5 h-3.5 mr-1" />
            Professeurs
          </TabsTrigger>
          <TabsTrigger value="students" data-testid="madrassa-manage-tab-students">
            <GraduationCap className="w-3.5 h-3.5 mr-1" />
            Élèves
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="madrassa-manage-tab-import">
            <Inbox className="w-3.5 h-3.5 mr-1" />
            Import CSV
          </TabsTrigger>
        </TabsList>

        {/* MEMBERS */}
        <TabsContent value="members" className="space-y-4 mt-4">
          {pending.length > 0 && (
            <div data-testid="madrassa-pending-list">
              <h4 className="text-sm font-serif text-text-primary mb-2">Demandes en attente</h4>
              <div className="space-y-2">
                {pending.map((m) => (
                  <div key={m.id} className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        {m.user_name || m.user_email}
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          {ROLE_LABEL[m.role] || m.role}
                        </Badge>
                      </p>
                      {m.notes && <p className="text-xs text-text-muted">{m.notes}</p>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => decide(m.id, 'reject')}
                            data-testid={`madrassa-pending-${m.id}-reject`}>
                      Refuser
                    </Button>
                    <Button size="sm" onClick={() => decide(m.id, 'approve')}
                            data-testid={`madrassa-pending-${m.id}-approve`}>
                      Approuver
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-serif text-text-primary mb-2">
              Membres approuvés ({approved.length})
            </h4>
            <div className="space-y-2">
              {approved.map((m) => (
                <div key={m.id} className="rounded-md border border-border bg-surface-elevated/40 p-3 flex items-center gap-3"
                     data-testid={`madrassa-member-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-primary truncate">
                      {m.user_name || m.user_email}
                    </p>
                    <p className="text-[11px] text-text-muted">
                      {m.user_email} · <Badge variant="outline" className="text-[10px]">
                        {ROLE_LABEL[m.role] || m.role}
                      </Badge>
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeMember(m.id)}
                          data-testid={`madrassa-member-${m.id}-remove`}>
                    <XCircle className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <GlassCard>
            <h4 className="text-sm font-serif text-text-primary mb-2">Inviter par email</h4>
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[220px]">
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="madrassa-invite-email"
                />
              </div>
              <select
                className="bg-surface-elevated/40 border border-border rounded-md p-2 text-sm"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                data-testid="madrassa-invite-role"
              >
                <option value="student">Élève</option>
                <option value="teacher">Professeur</option>
                <option value="admin">Admin</option>
              </select>
              <Button size="sm" onClick={invite} data-testid="madrassa-invite-submit">
                Inviter
              </Button>
            </div>
            <p className="text-[11px] text-text-muted mt-2">
              L'utilisateur doit déjà être inscrit. L'invitation est immédiatement approuvée.
            </p>
          </GlassCard>
        </TabsContent>

        {/* CLASSES */}
        <TabsContent value="classes" className="space-y-4 mt-4">
          <GlassCard>
            <h4 className="text-sm font-serif text-text-primary mb-2">Créer une classe</h4>
            <div className="flex flex-wrap gap-2 items-end">
              <Input
                placeholder="Nom (ex. Niveau 1)"
                value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                className="flex-1 min-w-[200px]"
                data-testid="madrassa-newclass-name"
              />
              <Input
                placeholder="Niveau (optionnel)"
                value={newClass.level}
                onChange={(e) => setNewClass({ ...newClass, level: e.target.value })}
                className="flex-1 min-w-[160px]"
                data-testid="madrassa-newclass-level"
              />
              <Button size="sm" onClick={createClass} data-testid="madrassa-newclass-submit">
                <Plus className="w-3.5 h-3.5 mr-1" /> Créer
              </Button>
            </div>
          </GlassCard>

          <div className="space-y-2">
            {classes.length === 0 && (
              <p className="text-sm text-text-muted">Aucune classe pour le moment.</p>
            )}
            {classes.map((c) => (
              <ClassRow
                key={c.id}
                madrassaId={madrassaId}
                cls={c}
                onChanged={refresh}
                onDelete={() => deleteClass(c.id)}
                approvedMembers={approved}
              />
            ))}
          </div>
        </TabsContent>

        {/* TEACHERS (Lot 35) */}
        <TabsContent value="teachers" className="space-y-4 mt-4">
          <MadrassaUsersTab
            madrassaId={madrassaId}
            role="teacher"
            classes={classes}
            testidPrefix="madrassa-teachers"
          />
        </TabsContent>

        {/* STUDENTS (Lot 35) */}
        <TabsContent value="students" className="space-y-4 mt-4">
          <MadrassaUsersTab
            madrassaId={madrassaId}
            role="student"
            classes={classes}
            testidPrefix="madrassa-students"
          />
        </TabsContent>

        {/* IMPORT */}
        <TabsContent value="import" className="space-y-4 mt-4">
          <GlassCard>
            <h4 className="text-sm font-serif text-text-primary mb-2">Import CSV</h4>
            <p className="text-xs text-text-muted mb-2">
              Colonnes : <code>firstName, lastName, email, role, className, classLevel</code>.
              Les colonnes <code>className/classLevel</code> sont optionnelles.
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setCsvPreview(null); setCsvCommitResult(null); }}
              className="text-xs"
              data-testid="madrassa-csv-input"
            />
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={onPreviewCsv} disabled={!csvFile}
                      data-testid="madrassa-csv-preview">
                Prévisualiser
              </Button>
              <Button size="sm" onClick={onCommitCsv} disabled={!csvFile}
                      data-testid="madrassa-csv-commit">
                Importer
              </Button>
            </div>
            {csvPreview && (
              <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3 text-xs space-y-1"
                   data-testid="madrassa-csv-preview-result">
                <p>{csvPreview.summary?.rows} lignes · {csvPreview.summary?.users_existing} utilisateurs trouvés ·
                  {' '}{csvPreview.summary?.users_missing} non inscrits</p>
                {csvPreview.summary?.classes_to_create?.length > 0 && (
                  <p>Classes à créer : {csvPreview.summary.classes_to_create.join(', ')}</p>
                )}
                {csvPreview.summary?.errors?.length > 0 && (
                  <p className="text-rose-300">
                    Erreurs : {csvPreview.summary.errors.join(' · ')}
                  </p>
                )}
              </div>
            )}
            {csvCommitResult && (
              <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs"
                   data-testid="madrassa-csv-commit-result">
                <p>{csvCommitResult.counts?.users_processed} utilisateurs traités ·
                  {' '}{csvCommitResult.counts?.memberships_created} memberships créées ·
                  {' '}{csvCommitResult.counts?.classes_created} classes créées ·
                  {' '}{csvCommitResult.counts?.class_memberships_created} affectations</p>
                {csvCommitResult.skipped_emails?.length > 0 && (
                  <p className="text-amber-300 mt-1">
                    Ignorés (non inscrits) : {csvCommitResult.skipped_emails.join(', ')}
                  </p>
                )}
              </div>
            )}
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ClassRow = ({ madrassaId, cls, onChanged, onDelete, approvedMembers }) => {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [addEmail, setAddEmail] = useState('');
  const [addRole, setAddRole] = useState('student');
  const [error, setError] = useState(null);

  const loadMembers = async () => {
    try {
      const r = await madrassaApi.listClassMembers(madrassaId, cls.id);
      setMembers(r.data?.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open) loadMembers(); /* eslint-disable-next-line */ }, [open]);

  const add = async () => {
    if (!addEmail) return;
    setError(null);
    try {
      await madrassaApi.addClassMember(madrassaId, cls.id, { email: addEmail, role: addRole });
      setAddEmail('');
      loadMembers(); onChanged();
    } catch (e) { setError(e?.response?.data?.detail || 'Erreur.'); }
  };
  const remove = async (uid) => {
    try { await madrassaApi.removeClassMember(madrassaId, cls.id, uid); loadMembers(); onChanged(); }
    catch (e) { setError(e?.response?.data?.detail || 'Erreur.'); }
  };

  return (
    <div className="rounded-md border border-border bg-surface-elevated/40 p-3"
         data-testid={`madrassa-class-row-${cls.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <GraduationCap className="w-4 h-4 text-primary" />
        <span className="text-sm text-text-primary">{cls.name}</span>
        {cls.level && <Badge variant="outline" className="text-[10px]">{cls.level}</Badge>}
        <Badge variant="outline" className="text-[10px]">
          {cls.teacher_count} prof · {cls.student_count} élève{cls.student_count > 1 ? 's' : ''}
        </Badge>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}
                data-testid={`madrassa-class-${cls.id}-toggle`}>
          {open ? 'Fermer' : 'Détails'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDelete}
                data-testid={`madrassa-class-${cls.id}-delete`}>
          <XCircle className="w-3.5 h-3.5" />
        </Button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <ClassStatsPanel classId={cls.id} />
          <div className="flex flex-wrap gap-2 items-end">
            <Input
              type="email"
              placeholder="email du membre"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              className="flex-1 min-w-[220px]"
              data-testid={`madrassa-class-${cls.id}-add-email`}
            />
            <select
              className="bg-surface-elevated/40 border border-border rounded-md p-2 text-sm"
              value={addRole}
              onChange={(e) => setAddRole(e.target.value)}
              data-testid={`madrassa-class-${cls.id}-add-role`}
            >
              <option value="student">Élève</option>
              <option value="teacher">Professeur</option>
            </select>
            <Button size="sm" onClick={add} data-testid={`madrassa-class-${cls.id}-add-submit`}>
              Ajouter
            </Button>
          </div>
          {error && <p className="text-xs text-rose-300">{error}</p>}
          <p className="text-[11px] text-text-muted">
            Le membre doit déjà être un {addRole === 'teacher' ? 'professeur' : 'élève'} approuvé
            de la madrassa ({approvedMembers.filter((m) => m.role === addRole).length} disponibles).
          </p>
          <div className="space-y-1">
            {members.map((m) => (
              <div key={m.id} className="text-xs flex items-center gap-2 py-1 border-b border-border/40">
                <span className="text-text-secondary truncate flex-1">
                  {m.user_name || m.user_email}
                </span>
                <Badge variant="outline" className="text-[10px]">
                  {ROLE_LABEL[m.role] || m.role}
                </Badge>
                <Button size="sm" variant="ghost" onClick={() => remove(m.user_id)}
                        data-testid={`madrassa-class-${cls.id}-member-${m.user_id}-remove`}>
                  <XCircle className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Teacher's view: list classes I teach + their stats panels.
// ---------------------------------------------------------------------------
const TeacherClassesSection = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    madrassaApi.myTeachingClasses()
      .then((r) => setClasses(r.data?.items || []))
      .catch(() => setClasses([]))
      .finally(() => setLoading(false));
  }, []);
  if (loading || classes.length === 0) return null;
  return (
    <div className="mt-6 space-y-3" data-testid="teacher-classes-section">
      <h3 className="text-sm font-serif text-text-primary">
        Mes classes (enseignant)
      </h3>
      {classes.map((c) => (
        <div
          key={c.id}
          className="rounded-md border border-border bg-surface-elevated/40 p-3"
          data-testid={`teacher-class-card-${c.id}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <GraduationCap className="w-4 h-4 text-primary" />
            <span className="text-sm text-text-primary">{c.name}</span>
            {c.level && (
              <Badge variant="outline" className="text-[10px]">{c.level}</Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {c.student_count} élève{c.student_count > 1 ? 's' : ''}
            </Badge>
            {c.madrassa?.name && (
              <span className="text-[11px] text-text-muted">· {c.madrassa.name}</span>
            )}
          </div>
          <ClassStatsPanel classId={c.id} />
        </div>
      ))}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export const MadrassaPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [active, setActive] = useState([]);
  const [partners, setPartners] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const isGlobalAdmin = user?.role === 'admin';

  const refresh = async () => {
    if (!isAuthenticated) { setLoading(false); return; }
    setLoading(true);
    try {
      const [me, act, par, st] = await Promise.all([
        madrassaApi.myMemberships().catch(() => ({ data: { items: [] } })),
        madrassaApi.listActive().catch(() => ({ data: { items: [] } })),
        madrassaApi.listPartners().catch(() => ({ data: { items: [] } })),
        madrassaApi.myStatus().catch(() => ({ data: null })),
      ]);
      setMemberships(me.data?.items || []);
      setActive(act.data?.items || []);
      setPartners(par.data?.items || []);
      setStatus(st.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="madrassa-anonymous">
        <SectionHeader
          overline="Madrassa"
          title="Connectez-vous pour accéder à votre madrassa"
          subtitle="Rejoignez ou administrez une madrassa pour collaborer avec vos enseignants et élèves."
        />
        <Button onClick={() => navigate('/login')} className="mt-4">Se connecter</Button>
      </div>
    );
  }

  if (openId) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <ManageMadrassa
          madrassaId={openId}
          onBack={() => { setOpenId(null); refresh(); }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="madrassa-page">
      <SectionHeader
        overline="Madrassa"
        title="Mes madrassas"
        subtitle="Rattachez-vous à une madrassa active ou demandez la création d'une nouvelle. Aucun professeur indépendant n'est autorisé."
      />

      <div className="mt-6 flex flex-wrap gap-2" data-testid="madrassa-actions">
        <JoinMadrassaDialog activeMadrassas={active} onDone={refresh} />
        {isGlobalAdmin && <CreateMadrassaDialog onDone={refresh} />}
        <Button variant="ghost" size="sm" onClick={refresh} data-testid="madrassa-refresh">
          <RefreshCcw className="w-3 h-3 mr-1" /> Rafraîchir
        </Button>
        {isGlobalAdmin && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/madrassa/admin')}
            data-testid="madrassa-go-admin"
          >
            <Shield className="w-3.5 h-3.5 mr-1" />
            Console admin global
          </Button>
        )}
      </div>

      <div className="mt-6">
        <TeacherOnboardingBanner status={status} partners={partners} onDone={refresh} />
      </div>

      {status?.teacherState === 'active' && status?.teachingClassesCount > 0 && (
        <TeacherClassesSection />
      )}

      <div className="mt-6 space-y-2">
        {loading && <p className="text-sm text-text-muted">Chargement…</p>}
        {!loading && memberships.length === 0 && (
          <p className="text-sm text-text-muted" data-testid="madrassa-memberships-empty">
            Vous n'avez aucune adhésion. Rejoignez une madrassa active ou demandez-en une nouvelle.
          </p>
        )}
        {memberships.map((m) => (
          <MembershipRow key={m.id} m={m} onOpen={() => setOpenId(m.madrassa_id)} />
        ))}
      </div>
    </div>
  );
};

export default MadrassaPage;
